/** 合并预测时使用：与服务端 troas/install 口径一致 → YYYY-MM-DD，对齐 dailyData.install_date 与 prediction horizons 的内层键 */
export const normalizeMergeInstallDateKey = (val) => {
  const raw = String(val ?? '').trim()
  if (!raw) return ''
  const head = raw.slice(0, 10)
  if (/^\d{8}$/.test(head)) {
    return `${head.slice(0, 4)}-${head.slice(4, 6)}-${head.slice(6, 8)}`
  }
  const m = head.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (!m) return head
  const [, y, mo, d] = m
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/**
 * 取出某一 install_date 在 predictions / predictionsAcc / roasAcc 下的档位对象。
 */
export const getPredictionTierForInstall = (tier, installDateRaw) => {
  if (!tier || installDateRaw == null || installDateRaw === '') return {}
  const norm = normalizeMergeInstallDateKey(installDateRaw)

  const resolveBlock = (v) => {
    if (v === undefined || v === null) return null
    return typeof v === 'object' ? v : {}
  }

  if (Object.prototype.hasOwnProperty.call(tier, installDateRaw)) {
    const b = resolveBlock(tier[installDateRaw])
    if (b !== null) return b
  }
  if (Object.prototype.hasOwnProperty.call(tier, norm)) {
    const b = resolveBlock(tier[norm])
    if (b !== null) return b
  }
  const foundKey = Object.keys(tier).find(
    (k) => normalizeMergeInstallDateKey(k) === norm,
  )
  const b = foundKey != null ? resolveBlock(tier[foundKey]) : null
  return b ?? {}
}

export const getPredictionHorizonEntries = (predictionData) => {
  if (predictionData?.horizons && Object.keys(predictionData.horizons).length > 0) {
    return Object.entries(predictionData.horizons)
      .map(([horizonKey, payload]) => {
        const p = typeof payload === 'object' && payload !== null ? payload : {}
        return {
          ...p,
          horizon: Number(horizonKey),
        }
      })
      .sort((a, b) => a.horizon - b.horizon)
  }
  return []
}

/** 合并预测数据到 install_date 维度 Map（与 roas-analysis-view 同源逻辑） */
export const mergePredictionDataByInstallDate = (predictionData) => {
  const horizonEntries = getPredictionHorizonEntries(predictionData)
  if (horizonEntries.length === 0) {
    return new Map()
  }

  const mergedMap = new Map()

  horizonEntries.forEach((entry) => {
    const hz = Number(entry.horizon)
    if (!Number.isFinite(hz) || hz <= 0) return
    const horizonDayKey = `day${hz}`
    const installDates = new Set([
      ...Object.keys(entry.predictions || {}),
      ...Object.keys(entry.predictionsAcc || {}),
      ...Object.keys(entry.roasAcc || {}),
    ])

    installDates.forEach((installDateRaw) => {
      const predictionsForDate = getPredictionTierForInstall(
        entry.predictions,
        installDateRaw,
      )
      const predictionsAccForDate = getPredictionTierForInstall(
        entry.predictionsAcc,
        installDateRaw,
      )
      const roasAccForDate = getPredictionTierForInstall(entry.roasAcc, installDateRaw)

      const modelKeys = Object.keys(predictionsForDate).length
        ? Object.keys(predictionsForDate)
        : Object.keys(predictionsAccForDate).length
          ? Object.keys(predictionsAccForDate)
          : Object.keys(roasAccForDate)

      if (modelKeys.length === 0) return

      const modelKey = modelKeys[0]

      const mergeKey = normalizeMergeInstallDateKey(installDateRaw)
      const mergedEntry =
        mergedMap.get(mergeKey) || {
          predictRevenueDays: {},
          predictRevenueAccDays: {},
          predictRoasAccDays: {},
          allModelsData: new Map(),
        }

      const revDaily = predictionsForDate[modelKey]?.[horizonDayKey]
      if (revDaily != null) {
        mergedEntry.predictRevenueDays[horizonDayKey] = Number(revDaily)
      }

      const revAcc = predictionsAccForDate[modelKey]?.[horizonDayKey]
      if (revAcc != null) {
        mergedEntry.predictRevenueAccDays[horizonDayKey] = Number(revAcc)
      }

      const roasAccVal = roasAccForDate[modelKey]?.[horizonDayKey]
      if (roasAccVal != null) {
        mergedEntry.predictRoasAccDays[horizonDayKey] = Number(roasAccVal)
      }

      const anchorData = {}
      if (revDaily != null) anchorData[horizonDayKey] = Number(revDaily)
      mergedEntry.allModelsData.set(`h${hz}:${modelKey}`, anchorData)

      mergedMap.set(mergeKey, mergedEntry)
    })
  })

  return mergedMap
}
